import { Flow } from '@/components/flow';
import { PanelProvider } from '@/context/panel-context';
import { useTreeStore } from '@/context/tree-context';
import { useEmitter } from '@/hooks/use-emitter';
import type { TreeState, TreeAction } from '@/tree-store';
import type { OnSelectionChangeParams } from 'reactflow';
import { getRootDataClass } from '@/features/explorer/helpers';
import { useUserConfig } from '@/hooks/user/use-user-config';
import { useDocumentTitle } from '@/hooks/use-document-title';

const treeSelector = (state: TreeState & TreeAction) => ({
  container: state.container,
  nodes: state.nodes,
  edges: state.edges,
  onNodesChange: state.onNodesChange,
  onEdgesChange: state.onEdgesChange,
});

export function ExplorerFlow({ id }: { id: string }) {
  const { data: userConfig } = useUserConfig();
  const setTitle = useDocumentTitle();
  const idPrefix = id.split('/').slice(0, -1).join('/');
  const emit = useEmitter({
    id: id,
    listenersPrefix: idPrefix,
  });
  const { nodes, edges, onNodesChange, onEdgesChange } =
    useTreeStore(treeSelector);

  function handleSelectionChange({ nodes }: OnSelectionChangeParams) {
    const selectedNodes = nodes.filter(
      (node) => node.type === 'object' || node.type === 'image'
    );

    if (selectedNodes.length === 0) {
      return;
    }

    const messages = [
      {
        type: 'ObjectCollection',
        data: selectedNodes.map((node) => ({
          uuid: node.data.uuid,
          container: node.data.root ? node.data.uuid : node.data.container,
          publicUrl: node.data?.publicUrl,
          root: node.data.root,
          dataClassUuid: node.data.root
            ? getRootDataClass(
                node.data.uuid,
                userConfig?.organisationConfig.dataClasses || []
              )
            : node.data.dataClassUuid,
        })),
      },
      {
        type: 'Preview',
        data: selectedNodes.map((node) => ({
          uuid: node.data.uuid,
          container: node.data.container,
          previewUuid: node.data.previewUuid,
        })),
      },
    ];

    emit(messages);

    setTitle(selectedNodes[0].data.label);
  }

  return (
    <PanelProvider>
      {nodes.length ? (
        <Flow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onSelectionChange={handleSelectionChange}
        />
      ) : null}
    </PanelProvider>
  );
}
